import { SharedFillRow } from './types';

export const COVER_FIT = { objectFit: 'cover' as const, width: '100%' as const, height: '100%' as const };

export const BORDER_RADIUS_8 = '8px';
export const NO_RADIUS = '0px';

/** Target tile width:height ratio for TILES layout.
 *  Matches 16:9 webcam feeds so `objectFit: cover` crops minimally.
 *  Row heights scale proportionally — wider rows become taller
 *  so every tile shares the same effective AR. */
export const TARGET_TILE_AR = 16 / 9;

export const SHARED_FILL_TILE_AR = 4 / 2;
export const PORTRAIT_SHARED_FILL_TILE_AR = 1;

export const PIP_POSITION_STYLES: Record<string, Record<string, string>> = {
  LEFT_TOP: { top: '1.5%', bottom: 'auto', left: '1%', right: 'auto' },
  LEFT_BOTTOM: { top: 'auto', bottom: '1.5%', left: '1%', right: 'auto' },
  RIGHT_TOP: { top: '1.5%', bottom: 'auto', left: 'auto', right: '1%' },
  RIGHT_BOTTOM: { top: 'auto', bottom: '1.5%', left: 'auto', right: '1%' },
  TOP_MIDDLE: { top: '1.5%', bottom: 'auto', left: '50%', right: 'auto', transform: 'translateX(-50%)' },
  BOTTOM_MIDDLE: { top: 'auto', bottom: '1.5%', left: '50%', right: 'auto', transform: 'translateX(-50%)' },
  LEFT_TOP_MIDDLE: { top: '50%', bottom: 'auto', left: '1%', right: 'auto', transform: 'translateY(-50%)' },
  RIGHT_TOP_MIDDLE: { top: '50%', bottom: 'auto', left: 'auto', right: '1%', transform: 'translateY(-50%)' }
};

export const PIP_TRANSITION = 'top 0.25s ease, left 0.25s ease, right 0.25s ease, bottom 0.25s ease';

export const GRID_DISTRIBUTION: Record<number, number[]> = {
  1: [1],
  2: [2],
  3: [2, 1],
  4: [2, 2],
  5: [2, 3],
  6: [3, 3],
  7: [3, 4],
  8: [2, 3, 3],
  9: [3, 3, 3],
  10: [3, 3, 4],
  11: [3, 4, 4],
  12: [3, 4, 5],
  13: [3, 5, 5],
  14: [3, 3, 4, 4],
  15: [3, 3, 4, 5],
  16: [3, 3, 5, 5],
  17: [3, 4, 5, 5],
  18: [3, 5, 5, 5],
  19: [4, 5, 5, 5],
  20: [5, 5, 5, 5],
  21: [4, 4, 4, 4, 5],
  22: [4, 4, 4, 5, 5],
  23: [4, 4, 5, 5, 5],
  24: [4, 5, 5, 5, 5]
};

export const GRID_DISTRIBUTION_PORTRAIT: Record<number, number[]> = {
  ...GRID_DISTRIBUTION,
  2: [1, 1],
  6: [2, 2, 2],
  7: [2, 2, 3]
};

export const GRID_DISTRIBUTION_PORTRAIT_WITH_SCREEN: Record<number, number[]> = {
  ...GRID_DISTRIBUTION,
  6: [2, 2, 2],
  7: [2, 2, 3]
};

export const SHARED_FILL_DISTRIBUTION: Record<number, SharedFillRow[]> = {
  1: [{ cols: 1, fullWidth: true, scaleRatio: 2.6 }],
  2: [
    { cols: 1, scaleRatio: 1.4 },
    { cols: 1, scaleRatio: 1.4 }
  ],
  3: [
    { cols: 1, fullWidth: true, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.8 }
  ],
  4: [
    { cols: 2, scaleRatio: 2 },
    { cols: 2, scaleRatio: 2 }
  ],
  5: [
    { cols: 1, fullWidth: true, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.5 },
    { cols: 2, scaleRatio: 1.5 }
  ],
  6: [
    { cols: 2, scaleRatio: 1.8 },
    { cols: 2, scaleRatio: 1.8 },
    { cols: 2, scaleRatio: 1.8 }
  ],
  7: [
    { cols: 1, fullWidth: true, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.4 }
  ],
  8: [
    { cols: 2, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.4 }
  ],
  9: [{ cols: 2 }, { cols: 2 }, { cols: 2 }, { cols: 3 }],
  10: [{ cols: 2 }, { cols: 2 }, { cols: 3 }, { cols: 3 }],
  11: [{ cols: 2 }, { cols: 3 }, { cols: 3 }, { cols: 3 }],
  12: [{ cols: 3 }, { cols: 3 }, { cols: 3 }, { cols: 3 }],
  13: [{ cols: 3 }, { cols: 3 }, { cols: 3 }, { cols: 3 }, { cols: 1 }],
  14: [{ cols: 3 }, { cols: 3 }, { cols: 3 }, { cols: 3 }, { cols: 2 }],
  15: [{ cols: 3 }, { cols: 3 }, { cols: 3 }, { cols: 3 }, { cols: 3 }],
  16: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }],
  17: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 1 }],
  18: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 2 }],
  19: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 3 }],
  20: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }],
  21: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 1 }],
  22: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 2 }],
  23: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 3 }],
  24: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }]
};
